import React, { useEffect, useState, useRef, ChangeEvent } from "react";
import { Button } from "./Button";
import { api, _registerFormNotOk, PLATFORM_ROUTE } from "./helpers";
import { Input } from "./Input";
import ACTIONS from "./EventLibrary";
import { rudderStackTrack } from "./Track";
import * as zxcvbn from "zxcvbn";
import * as Progress from '@radix-ui/react-progress';
import Link from "next/link";
import router from "next/router";
import { Callout } from "./Callout";
import { useUTMStore } from "./hooks/storeUTM";

declare global {
    // eslint-disable-next-line no-unused-vars
    interface Window {
        dataLayer: any;
    }
}
export function ApiSignup({ pageType, visitor, handleToast, adBlock, title = "Get started with Covalent API" }) {
    const [user, setUserForm] = useState({
        name: "",
        email: "",
        password: ""
    });
    const [termsAccepted, setTermsAccepted] = useState<boolean>(false);
    const utmStore = useUTMStore();
    const timerRef = useRef(0);
    const [verifyEmail, setVerify] = useState<boolean>(() => false);
    const [busy, setBusy] = useState<boolean>(false);
    const [load, setLoad] = useState<boolean>(false);

    const handleAgreeTerms = (e: ChangeEvent<HTMLInputElement>) => {
        const value: boolean = e.target.checked;
        setTermsAccepted(value);
    };

    const [passwordStrength, setStrength] = useState({
        password_strength: 0,
        password_strength_pct: 0,
        password_strength_hint: ""
    });

    useEffect(() => {
        return () => clearTimeout(timerRef.current);
    }, []);

    useEffect(() => {
        const keyDownHandler = event => {
            if (event.key === 'Enter') {
                event.preventDefault();
                handleSignup();
            }
        };

        document.addEventListener('keydown', keyDownHandler);

        return () => {
            document.removeEventListener('keydown', keyDownHandler);
        };
    }, [user]);


    const calculatePasswordStrength = (password) => {
        const result = zxcvbn(password);

        const pwS = {
            password_strength: 0,
            password_strength_pct: 0,
            password_strength_hint: ""
        };

        if (user.password.length > 0) {
            pwS.password_strength = result.score;
            pwS.password_strength_pct = Math.min(result.guesses_log10 * 7, 100);
            pwS.password_strength_hint = result.feedback.warning;
            if (pwS.password_strength < 3 && pwS.password_strength_hint.length === 0) {
                pwS.password_strength_hint = "Need a stronger password before you can continue";
            }
        }

        return pwS;
    };

    useEffect(() => {
        const newPwStrength = calculatePasswordStrength(user.password);
        setStrength(newPwStrength);
        setBusy(_registerFormNotOk(visitor.id, user.email, user.name, newPwStrength.password_strength));
    }, [user.password]);

    useEffect(() => {
        setBusy(_registerFormNotOk(visitor.id, user.email, user.name, passwordStrength.password_strength));
    }, [visitor, user.email, user.name]);

    const handleSignup = async () => {
        const latestPwStrength = calculatePasswordStrength(user.password);

        if (_registerFormNotOk(visitor.id, user.email, user.name, latestPwStrength.password_strength)) {
            return;
        }

        setLoad(true);
        rudderStackTrack(ACTIONS[`${pageType}_signup_attempt`], {
            value: {
                email: user.email,
                name: user.name
            }
        });
        const resp = await api.signup_covalent(user.name, user.email, user.password, visitor.id, visitor.visitor_locale, visitor.visitor_timezone, utmStore);
        
        if (!resp || resp.error) {
            if (resp?.error_code === 1002) {
                handleToast({
                    title: "Successfully signed up.",
                    description: "We have sent you a verification email."
                });
                rudderStackTrack(ACTIONS[`${pageType}_signup_success`], {
                    value: {
                        email: user.email,
                        name: user.name
                    }
                });
                window.dataLayer = window.dataLayer || [];
                window.dataLayer.push({
                    'event': 'signup-success',
                    'pageName': pageType,
                });
                if (pageType.includes("guide")) {
                    router.push(`/lp/${pageType}/thank-you?email=${encodeURI(user.email)}`);
                }
                setVerify(true);
                return;
            }

            handleToast({
                error: true,
                error_message: resp?.error_message
            });
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
                'event': 'signup-error',
                'pageName': pageType,
            });
            rudderStackTrack(ACTIONS[`${pageType}_signup_failed`], {
                value: {
                    email: user.email,
                    name: user.name
                }
            });
        } else {
            handleToast({
                title: "Successfully signed up.",
                description: "We have sent you a verification email."
            });

            rudderStackTrack(ACTIONS[`${pageType}_signup_success`], {
                value: {
                    email: user.email,
                    name: user.name
                }
            });
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
                'event': 'signup-success',
                'pageName': pageType,
            });
            if (pageType.includes("guide")) {
                router.push(`/lp/${pageType}/thank-you?email=${encodeURI(user.email)}`);
            }
            setVerify(true);
        }
        setLoad(false);
    };

    const handleResend = async () => {
        const resp = await api.resend_email_covalent(user.email);
        if (!resp || resp.error) {
            handleToast({
                error: true,
                error_message: resp.error_message
            });
        } else {
            handleToast({
                title: "Successfully resent verification email.",
                description: "Please check your email."
            });
        }
    };

    const pwStrengthVisualPercent = Math.floor(passwordStrength.password_strength_pct);

    return <>
        <div className="signup-container">
            {(title && <h2>{verifyEmail ? "Verify your email" : title}</h2>)}
            {adBlock &&
                <Callout title={undefined} type={"warning"}>
                    Please disable adblockers and VPNs. Some browsers like Brave are currently not supported.
                </Callout>
            }

            {
                verifyEmail ?
                    <>
                        <p>Thank you for signing up. We have sent you a verification email.</p>
                        <div onClick={handleResend} className="re-send">Resend verification email</div>
                    </>
                    :
                    <>
                        <Input type="email" label="Email" autocomplete="email" placeholder="Enter your email" onChange={(e) => {
                            setUserForm({ ...user, ...{ email: e.target.value } });

                        }} />
                        <Input id="name" type="input" label="Full Name" autocomplete="name" placeholder="Enter your name" onChange={(e) => {
                            setUserForm({ ...user, ...{ name: e.target.value } });
                        }} />
                        <div className="password-input">
                            <div className="inputs">
                                <Input fill={true} type="password" label="Password" autocomplete="new-password" placeholder="Enter your password" onChange={(e) => {
                                    setUserForm({ ...user, ...{ password: e.target.value } });
                                }} />
                                {(user.password && <div className="icon flex-center">
                                    {passwordStrength.password_strength < 3
                                        ? <div style={{ color: "var(--pink)" }}>✗</div>
                                        : <div style={{ color: "var(--teal)" }}>✓</div>
                                    }
                                </div>)}
                            </div>

                            {(user.password.length > 0 && <><Progress.Root className="ProgressRoot" value={passwordStrength.password_strength_pct}>
                                <Progress.Indicator
                                    className="ProgressIndicator"
                                    style={{
                                        transform: `translateX(-${100 - pwStrengthVisualPercent}%)`,
                                        backgroundColor: passwordStrength.password_strength <= 1 ? "red"
                                            : passwordStrength.password_strength <= 2 ? "yellow"
                                                : passwordStrength.password_strength <= 3 ? "green"
                                                    : "var(--teal)"
                                    }}
                                />
                            </Progress.Root>
                            <div className="hint">{passwordStrength.password_strength_hint}</div></>)}
                        </div>

                        <div className="terms-of-use-checkbox">
                            <input id="terms" onChange={handleAgreeTerms} checked={termsAccepted} type="checkbox"/>&nbsp;<label htmlFor="terms">I agree to Covalent's <Link target="_blank" href="https://www.covalenthq.com/terms-of-use/">Terms of Use</Link> and <Link target="_blank" href="https://www.covalenthq.com/privacy-policy/">Privacy Policy</Link>.</label>
                        </div>

                        <Button load={load ? true : undefined} fill={true} disabled={busy || load || !termsAccepted} onClick={handleSignup}>Sign me up</Button>
                        {(!pageType.includes("guide") && <Link href={`/${PLATFORM_ROUTE}/auth/login/`}>Have an account? Login here</Link>)}
                    </>

            }

        </div>
        <style jsx>
            {`
            .inputs{
                width:100%;
                position:relative;
            }
            .icon{
                position:absolute;
                right:-25px;
                display:flex;
                bottom:15px;
            }
            .separator {
                display: flex;
                align-items: center;
                text-align: center;
              }
            .separator::before,
            .separator::after {
                content: '';
                flex: 1;
                border-bottom: 1px solid var( --border-color);
            }
            .separator:not(:empty)::before {
                margin-right: .85em;
            }
            .separator:not(:empty)::after {
                margin-left: .85em;
            }
            .password-input{
                display:flex;
                flex-direction:column;
                gap:8px;
            }
            .hint{
                font-size:var(--font-size-1);
                height:10px;
            }
            h2{
                font-size: var(--font-size-6);
                line-height: var(--line-height-6);

            }
            .re-send{
                color:var(--pink);
            }
            .re-send:hover{
                cursor: pointer;
            }
            .signup-container{
                display:flex;
                justify-content:center;
                flex-direction: column;
                gap:${visitor.id === 0 ? "1rem" : "1.5rem"};
                width:100%;
            }
            
            `}
        </style>
    </>;
}
